import React, { useEffect, useMemo, useState } from 'react';
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	CardFooter,
	UncontrolledDropdown,
	DropdownToggle,
	DropdownMenu,
	DropdownItem,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import 'moment/locale/es';

import BarChart from '../../../../../Components/Common/Charts/BarChart';
import { generateColorGradient } from '../../../../../Components/Hooks/useColorArrayHooks';
import { getHourMeterSummary } from '../../../../../slices/thunks';
import { getConsumptionByEstablishment } from '../../../../../slices/fuelSupply/thunk';

moment.locale('es');

// ─── Helpers ────────────────────────────────────────────────────────────

const formatMap = {
	'2A': 'YYYY',
	'1A': 'YYYY',
	'6M': 'MM-YYYY',
	'3M': 'MM-YYYY',
	'1M': 'YYYY-[S]WW',
	AC: 'MM-YYYY',
};

const formatDate = (date, filter) => {
	return moment(date).format(formatMap[filter] || 'DD-MM-YYYY');
};

const getUniqueUnits = data => {
	if (!Array.isArray(data) || data.length === 0) return 'N/A';
	const uniqueUnits = new Set(
		data
			.map(item => item.unit_measurement_info?.name?.trim())
			.filter(Boolean)
			.map(unit => unit.charAt(0).toUpperCase() + unit.slice(1))
	);
	return uniqueUnits.size > 0 ? Array.from(uniqueUnits).join(', ') : 'N/A';
};

const tooltipFormatter = ({ series, seriesIndex, dataPointIndex, w }) => {
	const category = w.globals.labels[dataPointIndex];
	const generatorName = w.config.series[seriesIndex].name;
	const amount = w.config.series[seriesIndex].data[dataPointIndex].y;
	const unit = w.config.yaxis[0]?.title?.text || 'N/A';
	return `
        <div style="border-radius: 8px; padding: 10px; font-size: 11px; text-align: left; min-width: 200px; background: #fff; box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);">
            <div><strong>Fecha:</strong> ${category}</div>
            <div><strong>Generador:</strong> ${generatorName}</div>
            <div><strong>Consumo:</strong> ${amount.toLocaleString()} ${unit}</div>
        </div>
    `;
};

const prepareChartDataPower = (data, colorsChart, filter) => {
	const grouped = data.reduce((acc, item) => {
		if (!item.register_date || !item.power_generation_source_info) return acc;
		const date = formatDate(item.register_date, filter);
		const generator =
			item.power_generation_source_info.name || 'Generador Desconocido';
		const barge = item.feeding_barges_info?.name?.trim() || '';
		const key = barge ? `${barge} - ${generator}` : generator;
		const compositeKey = `${date}-${key}`;
		acc[compositeKey] = acc[compositeKey] || { date, key, hours: 0 };
		acc[compositeKey].hours += Number(item.hours) || 0;
		return acc;
	}, {});

	const sorted = Object.values(grouped).sort((a, b) =>
		moment(a.date).diff(moment(b.date))
	);
	const categories = [...new Set(sorted.map(d => d.date))];
	const generators = [...new Set(sorted.map(d => d.key))];

	if (colorsChart) {
		const colorMap = generators.reduce((acc, g, i) => {
			acc[g] = colorsChart[i % colorsChart.length];
			return acc;
		}, {});
	}

	const series = generators.map(name => ({
		name,
		data: categories.map(date => {
			const match = sorted.find(d => d.date === date && d.key === name);
			return { x: date, y: match?.hours || 0 };
		}),
	}));

	return {
		series,
		categories,
		colors: colorsChart ? generators.map(g => colorMap[g]) : undefined,
	};
};

const prepareChartDataBalance = (data, colorsChart, filter) => {
	const dateFormat = formatMap[filter] || 'DD-MM-YYYY';

	const grouped = data.reduce((acc, item) => {
		if (!item.register_date) return acc;
		const date = formatDate(item.register_date, filter);
		acc[date] = acc[date] || { date, hours: 0 };
		acc[date].hours += Number(item.hours) || 0;
		return acc;
	}, {});

	const sorted = Object.values(grouped).sort((a, b) =>
		moment(a.date, dateFormat).diff(moment(b.date, dateFormat))
	);

	const categories = sorted.map(d => d.date);

	return {
		series: [
			{
				name: 'Total Horas',
				data: categories.map(date => {
					const d = sorted.find(i => i.date === date);
					return { x: date, y: d?.hours || 0 };
				}),
			},
		],
		categories,
		colors: colorsChart ? [colorsChart[0]] : undefined,
	};
};

// ─── Componente Principal ────────────────────────────────────────────────

const HourMeterPowerGeneration = ({
	establishment,
	title,
	legendPosition = 'right',
	option,
	um = 'Horas',
	minHeight,
	maxHeight,
}) => {
	const dispatch = useDispatch();
	const [selectedFilter, setSelectedFilter] = useState(option);
	const [selectedGroup, setSelectedGroup] = useState('hour_power');

	const infoPower = useSelector(
		state => state.FuelSupply.hourmeterSummary?.hour_power || null
	);
	const infoHour = useSelector(
		state => state.FuelSupply.hourmeterSummary?.hour_balance || null
	);

	const [chartDataPower, setChartDataPower] = useState({
		series: [],
		categories: [],
		colors: [],
	});
	const [chartDataHour, setChartDataHour] = useState({
		series: [],
		categories: [],
		colors: [],
	});

	useEffect(() => {
		if (!establishment?._id) return;
		if (selectedGroup === 'hour_power') {
			dispatch(
				getHourMeterSummary({
					establishment_id: establishment._id,
					filter: selectedFilter,
					type: 'hour_power',
				})
			);
		} else {
			dispatch(
				getHourMeterSummary({
					establishment_id: establishment._id,
					filter: selectedFilter,
					type: 'hour_balance',
				})
			);
		}
	}, [establishment, selectedFilter, selectedGroup]);

	useEffect(() => {
		if (selectedGroup === 'hour_power' && Array.isArray(infoPower?.data)) {
			setChartDataPower(
				prepareChartDataPower(infoPower.data, undefined, selectedFilter)
			);
		} else if (
			selectedGroup === 'hour_balance' &&
			Array.isArray(infoHour?.data)
		) {
			setChartDataHour(
				prepareChartDataBalance(infoHour.data, undefined, selectedFilter)
			);
		}
	}, [infoPower?.data, infoHour?.data, selectedFilter, selectedGroup]);

	const noData =
		selectedGroup === 'hour_power'
			? infoPower?.data?.length === 0
			: infoHour?.data?.length === 0;

	const currentChartData =
		selectedGroup === 'hour_power' ? chartDataPower : chartDataHour;
	const currentYAxis = 'Horas';

	return (
		<Card className='m-0 h-100'>
			<CardHeader className='p-2 align-items-center d-flex'>
				<h5 className='card-title mb-0 flex-grow-1'>{title}</h5>
				<UncontrolledDropdown className='card-header-dropdown me-2'>
					<DropdownToggle
						tag='a'
						className='text-reset dropdown-btn'
						role='button'
					>
						<span className='fs-11'>Agrupado por: </span>
						<span className='text-muted fw-bold text-uppercase'>
							{selectedGroup === 'hour_power'
								? 'Fuente de Generación de Energía'
								: 'Consumo General'}
							<i className='mdi mdi-chevron-down ms-1'></i>
						</span>
					</DropdownToggle>
					<DropdownMenu className='dropdown-menu-end'>
						<DropdownItem
							onClick={() => setSelectedGroup('hour_balance')}
							active={selectedGroup === 'hour_balance'}
						>
							Consumo General
						</DropdownItem>
						<DropdownItem
							onClick={() => setSelectedGroup('hour_power')}
							active={selectedGroup === 'hour_power'}
						>
							Fuente de Generación de Energía
						</DropdownItem>
					</DropdownMenu>
				</UncontrolledDropdown>
				<div className='btn-box-dashboard'>
					{['2A', '1A', '6M', '3M', '1M'].map(f => (
						<Button
							key={f}
							color={selectedFilter === f ? 'primary' : 'secondary'}
							size='sm'
							className={`btn-soft-${
								selectedFilter === f ? 'primary' : 'secondary'
							} me-1`}
							onClick={() => setSelectedFilter(f)}
						>
							{f}
						</Button>
					))}
				</div>
			</CardHeader>

			{noData ? (
				<>
					<CardBody className='p-2'>
						<div className='d-flex justify-content-center align-items-center p-3'>
							<div className='text-center'>
								<div className='avatar-md'>
									<span className='avatar-title bg-info-subtle rounded-circle fs-1'>
										<i className='ri-gas-station-fill text-info'></i>
									</span>
								</div>
							</div>
						</div>
					</CardBody>
					<CardFooter className='m-0 p-2 text-center'>
						<h6 className='mb-0 p-0 text-wrap w-100'>
							<span className='fs-11 badge bg-info-subtle text-info text-wrap'>
								No cuenta con registro de consumo de combustible
							</span>
						</h6>
					</CardFooter>
				</>
			) : (
				<CardBody className='p-0'>
					<BarChart
						seriesChart={currentChartData.series}
						categoriesChart={currentChartData.categories}
						toolbarChart={true}
						height={'100%'}
						minHeight={minHeight}
						maxHeight={maxHeight}
						legendPosition={legendPosition || 'right'}
						legendOffsetY={2}
						strokeCurve={'smooth'}
						chartType={'bar'}
						horizontal={false}
						yAxisTitle={currentYAxis}
						um={um}
						tooltipFormatter={
							selectedGroup === 'hour_power'
								? tooltipFormatter
								: undefined
						}
						dataLabels={false}
					/>
				</CardBody>
			)}
		</Card>
	);
};

export default HourMeterPowerGeneration;
